import React, { useState } from "react";
import { useHistory } from "react-router";

import { ReactComponent as SearchSVG } from "App/common/icons-v2/search.svg";
import { FullyConfiguredDialogV2 } from "App/common/DialogV2";
import { SearchEntityList } from "App/components/SearchEntityList";
import { SearchEntityInput } from "App/components/SearchEntityInput";
import { SearchContextProvider } from "App/components/useEntities/useSearchContext";
import { useAuth } from "App/components/Auth";
import { useViewport } from "App/common/useViewport";
import {
  DEFAULT_COLUMNS,
  ENTITY_PREVIEW_METADATA,
} from "App/components/EntityTable";
import type { EntityRowConfig } from "App/components/EntityTable";
import { ENTITY_TYPE } from "App/components/EntityTable/config";
import { TabbedViewV2 } from "App/core/TabbedViewV2";
import { SHARE_KEY_PREFIX, Space } from "@highnote/server/src/core/entities";
import { useDiscoverPageItemsAsDefaultRows } from "./useDiscoverPageItemsAsDefaultRows";

import "./styles.scss";

const TAB = {
  LIBRARY: "library",
  HIGHNOTE: "highnote",
} as const;

type TabId = (typeof TAB)[keyof typeof TAB];

/**
 * A trigger styled like a search input that, on click, opens a dialog
 * containing tabs (Library / Highnote), a search field, and a list of
 * matching spaces. Clicking a result navigates to that space.
 *
 * Both tabs use the public-search Algolia token; the active tab determines
 * how results are filtered client-side.
 */
export const EntitySearch = ({ className }: { className?: string }) => {
  const history = useHistory();
  const { user } = useAuth();
  const { isMobile } = useViewport();
  const [isOpen, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>(TAB.LIBRARY);

  // Default rows shown on the Highnote tab when there's no active query.
  const { rows: highnoteDefaultRows } = useDiscoverPageItemsAsDefaultRows();

  const derivedShareKey = (space: Space): string | null => {
    const readableBy = space.readableByV2 || [];
    if (user && readableBy.includes(user.id)) return null;
    return (
      readableBy.find((entry) => entry.startsWith(SHARE_KEY_PREFIX)) ?? null
    );
  };

  const onSelect = (row: EntityRowConfig) => {
    if (row.type === ENTITY_TYPE.SPACE && row.id) {
      const shareKey = derivedShareKey(row.entity as Space);
      const url = shareKey
        ? `/space/${row.id}?shareKey=${shareKey}`
        : `/space/${row.id}`;
      history.push(url);
    } else if (row.type === ENTITY_TYPE.TRACK && row.id) {
      const track = row.entity as { spaceId?: string };
      if (track.spaceId) {
        history.push(`/space/${track.spaceId}/${row.id}`);
      }
    }
    setOpen(false);
  };

  /**
   * Algolia returns all spaces where visibility = "visible" OR the user is
   * directly listed in readableByV2 (see agolia.ts on the server).
   *
   * Client-side, we split those hits between the two tabs:
   * - Library: spaces where the user is a direct member.
   * - Highnote: spaces where space.visibility === "visible".
   *
   * Tracks are filtered out on the client in v1.
   */
  const filter = (row: EntityRowConfig) => {
    if (row.type !== ENTITY_TYPE.SPACE) return false;
    const space = row.entity as Space;
    if (activeTab === TAB.LIBRARY) {
      return (
        !!user &&
        ((space.readableByV2 || []).includes(user.id) ||
          space.createdBy === user.id)
      );
    }
    return space.visibility === "visible";
  };

  const tableBaseProps = {
    columns: [DEFAULT_COLUMNS.PREVIEW],
    entireRowClickable: true,
    onSelect,
    filter,
    /**
     * `transformRows` runs after filtering, before rendering. We use it here
     * to attach `entityPreviewMetadata` per row, which controls what the
     * EntityPreview cell renders below the title.
     *
     * - All spaces: creator name.
     * - Visible (Highnote) spaces: also include member count.
     */
    transformRows: (rows: EntityRowConfig[]) =>
      rows.map((row) => {
        const isVisibleSpace =
          row.type === ENTITY_TYPE.SPACE &&
          (row.entity as { visibility?: string }).visibility === "visible";
        return {
          ...row,
          entityPreviewMetadata: [
            ENTITY_PREVIEW_METADATA.CREATOR_NAME,
            ...(isVisibleSpace ? [ENTITY_PREVIEW_METADATA.MEMBER_COUNT] : []),
          ],
        };
      }),
  };

  // Algolia token requires an authenticated user — hide the search entry
  // entirely when logged out for now.
  if (!user) return null;

  // Mobile web doesn't get search; users are funneled to the native app.
  if (isMobile) return null;

  return (
    <>
      <button
        type="button"
        className={`entity-search-trigger ${className ?? ""}`}
        onClick={() => setOpen(true)}
        aria-label="Open search"
      >
        <SearchSVG />
        <span className="entity-search-trigger-placeholder">Search</span>
      </button>

      <FullyConfiguredDialogV2
        open={isOpen}
        onClose={() => setOpen(false)}
        header={{ title: "Search" }}
        contentProps={{
          removePadding: true,
          className: "entity-search-content",
        }}
        PaperProps={{ className: "entity-search-paper" }}
      >
        <SearchContextProvider tokenGetter="public">
          <div className="entity-search-input">
            <SearchEntityInput />
          </div>
          <div className="entity-search-tabs">
            <TabbedViewV2
              activeTabId={activeTab}
              disableStickyTabs
              tabs={[
                {
                  id: TAB.LIBRARY,
                  name: "Library",
                  onClick: () => setActiveTab(TAB.LIBRARY),
                },
                {
                  id: TAB.HIGHNOTE,
                  name: "Highnote",
                  onClick: () => setActiveTab(TAB.HIGHNOTE),
                },
              ]}
            />
          </div>
          <div className="entity-search-results">
            <SearchEntityList
              hideSearch
              libraryRoot={{ hideLibraryRoot: true }}
              tableBaseProps={tableBaseProps}
              defaultRows={
                activeTab === TAB.HIGHNOTE ? highnoteDefaultRows : undefined
              }
            />
          </div>
        </SearchContextProvider>
      </FullyConfiguredDialogV2>
    </>
  );
};
